import { useIdleTimer } from 'react-idle-timer'
import { useState, useEffect } from 'react'
import DraggableModal from '../../components/generic/DraggableModal'
import Typography from '@mui/material/Typography'
import StyledButton from '../../components/generic/StyledButton'
import Stack from '@mui/material/Stack'

// events that can reset idle timer
const events = ['keydown', 'mousedown']

// idle time before logout (10 minutes)
const maxIdleMilliseconds = 6600_000

// time before logout when we should display prompt (5 minutes)
const promptBeforeIdle = 120_000

/**
 * Formats seconds into a human-readable string (e.g., "2 minutes, 30 seconds")
 * @param {number} seconds - Time in seconds to format
 * @returns {string} Formatted time string
 */
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const offsetSeconds = seconds - 60 * minutes
  let minuteText = minutes ? `${minutes} minutes, ` : ''
  let secondText = `${offsetSeconds} seconds`
  if (minutes == 1) {
    minuteText = `${minutes} minute, `
  }
  if (offsetSeconds == 1) {
    secondText = `${offsetSeconds} second`
  }
  return `${minuteText}${secondText}`
}

/**
 * InactivityTracker
 *
 * Tracks user inactivity and automatically logs users out after a specified
 * period of inactivity. Shows a warning modal before logout occurs.
 *
 * @component
 * @param {React.ReactNode} children - Child components to be wrapped by the inactivity tracker
 * @returns {React.ReactElement} The wrapped components with inactivity tracking functionality
 */
const InactivityTracker = ({ children }) => {
  const [remaining, setRemaining] = useState(maxIdleMilliseconds)
  const [modalVisible, setModalVisible] = useState(false)

  const logout = () => {
    setModalVisible(false)
    window.location.href = '/logout'
  }

  const onIdle = () => {
    logout()
  }

  const onActive = () => {
    setModalVisible(false)
  }

  const onPrompt = () => {
    setModalVisible(true)
  }

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle: onIdle,
    onActive: onActive,
    onPrompt: onPrompt,
    timeout: maxIdleMilliseconds,
    promptBeforeIdle: promptBeforeIdle,
    events: events,
    throttle: 500,
  })

  const handleStillHere = () => {
    activate()
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000))
    }, 500)
    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <>
      {children}

      {modalVisible && (
        <DraggableModal
          hideClose={true}
          modalVisible={modalVisible}
          handleCancelModal={() => {
            setModalVisible(false)
          }}
          modalTitle='Are you still there?'
          content={<Typography>{`You will be logged out in ${formatTime(remaining)}.`}</Typography>}
          actions={
            <Stack direction='row' spacing={3} sx={{ width: '100%' }}>
              <StyledButton onClick={logout} label='Logout' fontSize={14} customTheme='outlined' />
              <StyledButton onClick={handleStillHere} label='Yes' fontSize={14} />
            </Stack>
          }
        />
      )}
    </>
  )
}

export default InactivityTracker
